import SvgLayer from "./SvgLayer";
import React, { useEffect, useState, useRef } from "react";
import { DraggableCore, DraggableData } from "react-draggable";
import {
  MouseTouchEvent,
  MouseEvent,
} from "react-draggable/build/cjs/utils/types";

import {
  getOffset,
  getOffsetProc,
  offsetToKey,
  getPosFromKey,
  key2pos,
} from "../chessUtils/util";
import { ANIMATION_DURATION, Z_UNDER } from "./animations";
import DrawLayer from "./DrawLayer";
import { Key } from "./types";
import { MoveShort, Square } from "@chesshotel/chess";

type MoveShortWithAudit = MoveShort & {
  audit?: {
    source: string;
    pointerType: string | null;
  };
};

const isSafari =
  process.browser &&
  /Safari/.test(navigator.userAgent) &&
  /Apple Computer/.test(navigator.vendor);

const DRAG_CENTER_THRESHOLD_PX = 2;

export const isRightButton = (e: MouseTouchEvent): boolean =>
  e.buttons === 2 || e.button === 2;

export default function DragBoard({
  freeMove,
  lastPuzzleMove,
  puzzleDone,
  pieceRefs,
  movePiece,
  possibleMoves,
  squareSize,
  playingWhite,
  position,
  highLightKey,
  bestMove,
  chessTermArrows,
  showLastMove,
  lastMove,
  lastMoveColor,
  settings,
  moveType,
  moveTypeKey,
  children,
  highlightSquares,
  allowPremoves = false,
  premove,
  onPremoveSet,
  onPremoveClear,
}: {
  freeMove: boolean;
  pieceRefs: any;
  possibleMoves: any;
  squareSize: number;
  playingWhite: boolean;
  highLightKey: Key | null;
  movePiece: any;
  position: any;
  bestMove: MoveShort | null;
  chessTermArrows: any;
  showLastMove: boolean;
  lastMove: any;
  lastMoveColor: any;
  settings: any;
  lastPuzzleMove: any;
  puzzleDone: boolean;
  moveType: any;
  moveTypeKey: Key | null;
  highlightSquares: Square[];
  allowPremoves?: boolean;
  premove?: MoveShort | null;
  onPremoveSet?: (move: MoveShort) => void;
  onPremoveClear?: () => void;
  children: React.ReactNode;
}) {
  const disableInteractions = !squareSize;
  const [goast, setGoast] = useState(null);
  const [moveHelp, setMoveHelp] = useState(null);
  const [moveSquarePos, setMoveSquarePos] = useState(null);
  const [shapes, setShapes] = useState([]);

  const [selectedKey, setSelectedKey] = useState<Key | null>(null);
  const [hoveredKey, setHoveredKey] = useState<Key | null>(null);
  const [drawMode, setDrawMode] = useState(false);
  const boardRef = useRef<HTMLDivElement>(null);

  // Use refs instead of module-level variables to prevent stuck pieces
  const lastDragKeyRef = useRef<Key | null>(null);
  const currentKeyRef = useRef<Key | null>(null);
  const dragIdRef = useRef<string | null>(null);
  const dragPointerOffsetRef = useRef({
    x: squareSize / 2,
    y: squareSize / 2,
  });
  const dragStartPointerRef = useRef({
    x: 0,
    y: 0,
  });
  const dragStartedRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const wasSelectedRef = useRef<Key | null>(null);

  function isOwnPiece(key: Key) {
    const piece = position?.[key];
    return piece && (freeMove ||
      (playingWhite && piece.color === "white") ||
      (!playingWhite && piece.color === "black"));
  }

  function clearSelection() {
    setSelectedKey(null);
    setMoveHelp(null);
    setHoveredKey(null);
    setMoveSquarePos(null);
  }

  function clearDragRefs() {
    lastDragKeyRef.current = null;
    dragIdRef.current = null;
    currentKeyRef.current = null;
    dragPointerOffsetRef.current = {
      x: squareSize / 2,
      y: squareSize / 2,
    };
    dragStartPointerRef.current = {
      x: 0,
      y: 0,
    };
    dragStartedRef.current = false;
  }

  function resetDragState({ keepMoveHelp = false }: { keepMoveHelp?: boolean } = {}) {
    const dragId = dragIdRef.current;
    if (dragId) {
      const pieceRef = pieceRefs.current[dragId];
      if (pieceRef) {
        pieceRef.style.zIndex = Z_UNDER;
        const pieceEntry = Object.entries(position || {}).find(
          ([, value]: any) => value && value.id === dragId,
        );
        if (pieceEntry) {
          const goal = getOffset(pieceEntry[0] as Key, playingWhite, squareSize);
          pieceRef.style.transform = `translate(${goal.x}px, ${goal.y}px)`;
        }
      }
    }
    setGoast(null);
    setMoveSquarePos(null);
    if (!keepMoveHelp && settings.pieceDestinations) {
      setMoveHelp(null);
    }
    clearDragRefs();
  }

  function canMoveFrom(from: Key, to: Key) {
    const moves = possibleMoves?.[from];
    return Array.isArray(moves) && moves.indexOf(to) >= 0;
  }

  function canMove(to: Key) {
    return canMoveFrom(currentKeyRef.current, to);
  }

  function isBoardSquare(file: number, rank: number) {
    return file >= 1 && file <= 8 && rank >= 1 && rank <= 8;
  }

  function makeKey(file: number, rank: number): Key {
    return `${"abcdefgh"[file - 1]}${rank}` as Key;
  }

  function isFriendlyTarget(from: Key, to: Key) {
    const fromPiece = position?.[from];
    const toPiece = position?.[to];
    return Boolean(fromPiece && toPiece && fromPiece.color === toPiece.color);
  }

  function addPremoveTarget(
    targets: Key[],
    from: Key,
    file: number,
    rank: number
  ) {
    if (!isBoardSquare(file, rank)) return false;
    const target = makeKey(file, rank);
    if (isFriendlyTarget(from, target)) return false;
    targets.push(target);
    return !position?.[target];
  }

  function getPremoveDestinations(from: Key) {
    if (!allowPremoves || !isOwnPiece(from)) return [];

    const piece = position?.[from];
    if (!piece) return [];

    const targets: Key[] = [];
    const [file, rank] = key2pos(from);

    if (piece.role === "pawn") {
      const direction = piece.color === "white" ? 1 : -1;
      const startRank = piece.color === "white" ? 2 : 7;
      const oneForwardRank = rank + direction;
      const oneForward = makeKey(file, oneForwardRank);

      if (isBoardSquare(file, oneForwardRank) && !position?.[oneForward]) {
        targets.push(oneForward);
        const twoForwardRank = rank + direction * 2;
        const twoForward = makeKey(file, twoForwardRank);
        if (
          rank === startRank &&
          isBoardSquare(file, twoForwardRank) &&
          !position?.[twoForward]
        ) {
          targets.push(twoForward);
        }
      }

      [file - 1, file + 1].forEach((captureFile) => {
        if (!isBoardSquare(captureFile, rank + direction)) return;
        const target = makeKey(captureFile, rank + direction);
        if (!isFriendlyTarget(from, target)) {
          targets.push(target);
        }
      });

      return targets;
    }

    const addLeaperTargets = (deltas: number[][]) => {
      deltas.forEach(([fileDelta, rankDelta]) => {
        addPremoveTarget(targets, from, file + fileDelta, rank + rankDelta);
      });
    };

    const addSliderTargets = (deltas: number[][]) => {
      deltas.forEach(([fileDelta, rankDelta]) => {
        let nextFile = file + fileDelta;
        let nextRank = rank + rankDelta;

        while (isBoardSquare(nextFile, nextRank)) {
          const keepGoing = addPremoveTarget(targets, from, nextFile, nextRank);
          if (!keepGoing) break;
          nextFile += fileDelta;
          nextRank += rankDelta;
        }
      });
    };

    if (piece.role === "knight") {
      addLeaperTargets([
        [1, 2],
        [2, 1],
        [2, -1],
        [1, -2],
        [-1, -2],
        [-2, -1],
        [-2, 1],
        [-1, 2],
      ]);
    } else if (piece.role === "bishop") {
      addSliderTargets([
        [1, 1],
        [1, -1],
        [-1, -1],
        [-1, 1],
      ]);
    } else if (piece.role === "rook") {
      addSliderTargets([
        [1, 0],
        [0, -1],
        [-1, 0],
        [0, 1],
      ]);
    } else if (piece.role === "queen") {
      addSliderTargets([
        [1, 0],
        [1, 1],
        [0, 1],
        [-1, 1],
        [-1, 0],
        [-1, -1],
        [0, -1],
        [1, -1],
      ]);
    } else if (piece.role === "king") {
      addLeaperTargets([
        [1, 0],
        [1, 1],
        [0, 1],
        [-1, 1],
        [-1, 0],
        [-1, -1],
        [0, -1],
        [1, -1],
      ]);
    }

    return targets;
  }

  function canPremoveFrom(from: Key, to: Key) {
    return (
      allowPremoves &&
      Boolean(onPremoveSet) &&
      from &&
      to &&
      from !== to &&
      getPremoveDestinations(from).includes(to)
    );
  }

  function getMoveHelpForKey(key: Key) {
    const legalMoves = possibleMoves?.[key];
    if (Array.isArray(legalMoves) && legalMoves.length > 0) {
      return legalMoves;
    }
    const premoveDests = getPremoveDestinations(key);
    return premoveDests.length > 0 ? premoveDests : null;
  }

  function getPointerType(e?: MouseTouchEvent) {
    if (!e) return null;
    if ("pointerType" in e && typeof e.pointerType === "string") {
      return e.pointerType;
    }
    if ("touches" in e || "changedTouches" in e) {
      return "touch";
    }
    return "mouse";
  }

  function buildMoveAudit(source: string, e?: MouseTouchEvent) {
    return {
      source,
      pointerType: getPointerType(e),
    };
  }

  function buildPremove(
    from: Key,
    to: Key,
    source: string,
    e?: MouseTouchEvent
  ): MoveShortWithAudit {
    const piece = position?.[from];
    const promotionRank = piece?.color === "white" ? "8" : "1";
    const promotion =
      piece?.role === "pawn" && to[1] === promotionRank ? "q" : undefined;

    return {
      from: from as MoveShort["from"],
      to: to as MoveShort["to"],
      promotion,
      audit: buildMoveAudit(source, e),
    };
  }

  function queuePremove(from: Key, to: Key, source: string, e?: MouseTouchEvent) {
    onPremoveSet?.(buildPremove(from, to, source, e));
    clearSelection();
  }

  function clearPremoveInteraction() {
    if (premove) {
      onPremoveClear?.();
    }
  }

  function getHoveredKeyFromEvent(
    e: React.MouseEvent<HTMLDivElement, globalThis.MouseEvent>
  ): Key | null {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect || !squareSize) return null;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    if (x < 0 || y < 0 || x >= rect.width || y >= rect.height) {
      return null;
    }

    return offsetToKey(x, y, playingWhite, squareSize) as Key;
  }

  function onBoardMouseMove(
    e: React.MouseEvent<HTMLDivElement, globalThis.MouseEvent>
  ) {
    // During drag-and-drop, onDrag controls moveSquarePos.
    if (dragIdRef.current) {
      return;
    }

    if (!selectedKey) {
      if (hoveredKey) {
        setHoveredKey(null);
      }
      if (moveSquarePos) {
        setMoveSquarePos(null);
      }
      return;
    }

    const key = getHoveredKeyFromEvent(e);
    if (key !== hoveredKey) {
      setHoveredKey(key);
      if (
        key &&
        (canMoveFrom(selectedKey, key) || canPremoveFrom(selectedKey, key))
      ) {
        setMoveSquarePos(getPosFromKey(key, playingWhite));
      } else {
        setMoveSquarePos(null);
      }
    }
  }

  useEffect(() => {
    if (dragIdRef.current && possibleMoves[currentKeyRef.current]) {
      if (canMove(lastDragKeyRef.current as Key)) {
        const dragPos = getPosFromKey(lastDragKeyRef.current as Key, playingWhite);
        setMoveSquarePos(dragPos);
      }
    }
  }, [possibleMoves]);

  useEffect(() => {
    if (!dragIdRef.current || !currentKeyRef.current) {
      return;
    }
    const currentPiece = position?.[currentKeyRef.current];
    if (!currentPiece || currentPiece.id !== dragIdRef.current) {
      // Board state changed while dragging (e.g. server correction): clear stale drag state.
      resetDragState();
    }
  }, [position, playingWhite, squareSize]);

  useEffect(() => {
    if (!premove) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") {
        return;
      }
      e.preventDefault();
      clearSelection();
      onPremoveClear?.();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [premove, onPremoveClear]);

  function onDrag(e, data) {
    if (!dragIdRef.current) {
      return;
    }

    if (!dragStartedRef.current) {
      const dx = data.x - dragStartPointerRef.current.x;
      const dy = data.y - dragStartPointerRef.current.y;
      const movedDistance = Math.hypot(dx, dy);

      if (movedDistance < DRAG_CENTER_THRESHOLD_PX) {
        return;
      }

      dragStartedRef.current = true;
    }

    hasDraggedRef.current = true;

    const pointerOffset = dragPointerOffsetRef.current;
    const posProc = {
      x: ((data.x - pointerOffset.x) * 100) / squareSize,
      y: ((data.y - pointerOffset.y) * 100) / squareSize,
    };

    const pieceRef = pieceRefs.current[dragIdRef.current];
    if (pieceRef) {
      pieceRef.style.transform = `translate(${posProc.x}%, ${posProc.y}%)`;
    }
    const dragKey = offsetToKey(
      data.x,
      data.y,
      playingWhite,
      squareSize
    ) as Key;

    if (lastDragKeyRef.current != dragKey) {
      if (
        canMove(dragKey) ||
        canPremoveFrom(currentKeyRef.current, dragKey)
      ) {
        setMoveSquarePos(getPosFromKey(dragKey, playingWhite));
      } else {
        setMoveSquarePos(null);
      }
      lastDragKeyRef.current = dragKey;
    }
  }

  function onStop(e: MouseTouchEvent, data: DraggableData) {
    if (!dragIdRef.current) {
      return;
    }
    setMoveSquarePos(null);
    clearPremoveInteraction();

    const to = offsetToKey(data.x, data.y, playingWhite, squareSize) as Key;
    const pieceRef = pieceRefs.current[dragIdRef.current];
    if (canMove(to)) {
      const goal = getOffsetProc(to, playingWhite);
      if (pieceRef) {
        pieceRef.style.transform = `translate(${goal.x}%, ${goal.y}%)`;
      }
      movePiece({
        from: currentKeyRef.current,
        to,
        audit: buildMoveAudit("normal_drag", e),
      });
      onPremoveClear?.();
    } else if (canPremoveFrom(currentKeyRef.current, to)) {
      const goal = getOffset(currentKeyRef.current, playingWhite, squareSize);
      if (pieceRef) {
        pieceRef.style.transform = `translate(${goal.x}px, ${goal.y}px)`;
      }
      queuePremove(currentKeyRef.current, to, "premove_drag", e);
    } else if (to === currentKeyRef.current && isOwnPiece(currentKeyRef.current)) {
      // Ended on same square — treat as click to select/deselect
      const goal = getOffset(currentKeyRef.current, playingWhite, squareSize);
      if (pieceRef) {
        pieceRef.style.transform = `translate(${goal.x}px, ${goal.y}px)`;
      }
      if (pieceRef && pieceRefs.current[dragIdRef.current]) {
        pieceRefs.current[dragIdRef.current].style.zIndex = Z_UNDER;
      }
      setGoast(null);
      if (wasSelectedRef.current === currentKeyRef.current) {
        // Was already selected — deselect
        wasSelectedRef.current = null;
        setMoveHelp(null);
        setSelectedKey(null);
        setHoveredKey(null);
      } else {
        setSelectedKey(currentKeyRef.current);
        settings.pieceDestinations &&
          setMoveHelp(getMoveHelpForKey(currentKeyRef.current));
      }
      clearDragRefs();
      return;
    } else {
      const goal = getOffset(currentKeyRef.current, playingWhite, squareSize);
      if (pieceRef) {
        pieceRef.style.transform = `translate(${goal.x}px, ${goal.y}px)`;
      }
    }

    if (pieceRef && pieceRefs.current[dragIdRef.current]) {
      pieceRefs.current[dragIdRef.current].style.zIndex = Z_UNDER;
    }

    setGoast(null);
    settings.pieceDestinations && setMoveHelp(null);
    clearDragRefs();
  }

  function onStartDrag(e: MouseTouchEvent, data: DraggableData) {
    if (isSafari) {
      e.preventDefault();
    }
    if (!position) {
      return;
    }
    const startKey = offsetToKey(
      data.x,
      data.y,
      playingWhite,
      squareSize
    ) as Key;

    // Click-to-move: if a piece is selected, handle the second click
    if (selectedKey) {
      if (canMoveFrom(selectedKey, startKey)) {
        // Animate the piece to the destination
        const selectedPiece = position[selectedKey];
        if (selectedPiece) {
          const pieceEl = pieceRefs.current[selectedPiece.id];
          if (pieceEl) {
            const start = getOffset(selectedKey, playingWhite, squareSize);
            const goal = getOffset(startKey, playingWhite, squareSize);
            pieceEl.style.zIndex = 300;
            // Set final position as inline style so it takes effect when animation ends
            pieceEl.style.transform = `translate(${goal.x}px, ${goal.y}px)`;
            pieceEl.animate(
              [
                { transform: `translate(${start.x}px, ${start.y}px)` },
                { transform: `translate(${goal.x}px, ${goal.y}px)` },
              ],
              {
                duration: ANIMATION_DURATION,
                easing: "ease-in-out",
              }
            );
          }
        }
        movePiece({
          from: selectedKey,
          to: startKey,
          audit: buildMoveAudit("normal_click", e),
        });
        onPremoveClear?.();
        clearSelection();
        return;
      }
      if (canPremoveFrom(selectedKey, startKey)) {
        queuePremove(selectedKey, startKey, "premove_click", e);
        clearSelection();
        return;
      }
      clearPremoveInteraction();
      wasSelectedRef.current = selectedKey === startKey ? startKey : null;
      clearSelection();
      // Fall through to drag setup — allows dragging the selected piece directly
    }

    if (!isOwnPiece(startKey)) {
      clearPremoveInteraction();
    }

    hasDraggedRef.current = false;
    currentKeyRef.current = startKey;
    const piece = position[startKey];
    if (isOwnPiece(startKey)) {
      dragPointerOffsetRef.current = {
        x: squareSize / 2,
        y: squareSize / 2,
      };
      dragStartPointerRef.current = {
        x: data.x,
        y: data.y,
      };
      dragStartedRef.current = false;

      const { color, role } = piece;
      dragIdRef.current = piece.id;
      const pieceRef = pieceRefs.current[piece.id];
      if (pieceRef) {
        pieceRef.style.zIndex = 300;

        // Center the dragged piece under the cursor as soon as pickup starts.
        const setCenteredTransform = () => {
          if (dragIdRef.current !== piece.id) {
            return;
          }
          const centeredPos = {
            x: ((data.x - squareSize / 2) * 100) / squareSize,
            y: ((data.y - squareSize / 2) * 100) / squareSize,
          };
          pieceRef.style.transform = `translate(${centeredPos.x}%, ${centeredPos.y}%)`;
        };

        setCenteredTransform();
      }
      setGoast({
        goastPos: getPosFromKey(startKey, playingWhite),
        goastPiece: { color, role },
      });

      settings.pieceDestinations && setMoveHelp(getMoveHelpForKey(startKey));
    }
  }

  function startDraw(e: MouseEvent) {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = (e as unknown as globalThis.MouseEvent).clientX - rect.left;
    const y = (e as unknown as globalThis.MouseEvent).clientY - rect.top;
    const currentPos = [Math.floor(x / squareSize), Math.floor(y / squareSize)];
    setShapes([...shapes, { from: currentPos }]);
  }

  function onDraw(e: MouseTouchEvent, data: DraggableData) {
    const currentPos = [
      Math.floor(data.x / squareSize),
      Math.floor(data.y / squareSize),
    ];
    const newShapes = [...shapes];
    newShapes[newShapes.length - 1].to = currentPos;
    setShapes(newShapes);
  }
  function onStopDraw(e: MouseTouchEvent, data: DraggableData) {}

  const canUseHoveredSquare = Boolean(
    selectedKey &&
      hoveredKey &&
      (canMoveFrom(selectedKey, hoveredKey) ||
        canPremoveFrom(selectedKey, hoveredKey))
  );

  const boardContent = (
    <div
      ref={boardRef}
      className="relative flex w-full h-full"
      style={{
        cursor: canUseHoveredSquare ? "pointer" : "default",
      }}
      onMouseMove={onBoardMouseMove}
      onMouseLeave={() => {
        setHoveredKey(null);
        setMoveSquarePos(null);
      }}
      onContextMenu={(e) => {
        if (!premove) {
          return;
        }
        e.preventDefault();
        clearSelection();
        onPremoveClear?.();
      }}
    >
      <DrawLayer
        lastPuzzleMove={lastPuzzleMove}
        goast={goast}
        moveSquarePos={moveSquarePos}
        moveHelp={moveHelp}
        position={position}
        highLightKey={highLightKey}
        playingWhite={playingWhite}
        showLastMove={showLastMove}
        lastMove={lastMove}
        lastMoveColor={lastMoveColor}
        settings={settings}
        puzzleDone={puzzleDone}
        moveType={moveType}
        moveTypeKey={moveTypeKey}
        highlightSquares={highlightSquares}
        squareSize={squareSize}
        selectedKey={selectedKey}
        premove={allowPremoves ? premove : null}
      />
      <SvgLayer
        shapes={shapes}
        bestMove={bestMove}
        chessTermArrows={chessTermArrows}
        playingWhite={playingWhite}
      />
      {children}
    </div>
  );

  if (disableInteractions) {
    return boardContent;
  }

  return (
    <>
      <DraggableCore
        enableUserSelectHack={false}
        onStart={(e: MouseTouchEvent, data: DraggableData) => {
          if (!e.shiftKey) {
            onStartDrag(e, data);
          }
        }}
        onDrag={(e: MouseTouchEvent, data: DraggableData) => {
          drawMode ? onDraw(e, data) : onDrag(e, data);
        }}
        onStop={(e: MouseTouchEvent, data: DraggableData) => {
          drawMode ? onStopDraw(e, data) : onStop(e, data);
        }}
        onMouseDown={(e: MouseEvent) => {
          if (e.shiftKey) {
            setDrawMode(true);
            startDraw(e);
          } else {
            setDrawMode(false);
            setShapes([]);
          }
        }}
      >
        {boardContent}
      </DraggableCore>
    </>
  );
}
